import { CREATE_SPRITE_WORKER_URL } from '../../config.js';
import { fetchCache } from '../../utils/fetchCache.js';
import { tokenManager } from '../../utils/TokenManager.js';
import {
  buildLocalCreditResult,
  computeImageUnitCost,
  formatCreditsUsageLine,
  getCharacterHasLora,
  getCharacterStyle,
  loadCreditPricing
} from '../../create/creditCostApi.js';
import {
  getBlockedFilteredImagePromptMessage,
  getBlockedUnrecognizedPromptMessage
} from '../../create/danbooruTags.js';
import { initializePromptAutocomplete } from '../../create/promptAutocomplete.js';
import { syncRichPromptEditorDisabledState } from '../../create/richPromptEditor.js';
import { initPromptHelpers } from '../../create/utils/promptHelpersUi.js';
import { formatSpriteNameForPublish } from '../../create/utils/spriteDisplay.js';
import { syncSpriteCredits } from '../../create/videoScheduleApi.js';
import { HangoutState } from '../state/HangoutState.js';
import { hangoutWorkerUrl, makeWorkerRequest } from './hangout-worker-request.js';
import { showNotification } from './services/spriteUpgradeService.js';

const MODAL_ID = 'message-image-prompt-modal';
let promptAutocompleteReady = false;

const ensurePromptAutocomplete = async () => {
  if (promptAutocompleteReady) return;
  await initializePromptAutocomplete('message-image-prompt-text', 'spriteActionPrompt', null, {});
  promptAutocompleteReady = true;
};

const setPromptEnabled = (promptEl, enabled) => {
  promptEl.disabled = !enabled;
  syncRichPromptEditorDisabledState(promptEl);
};

const setFormPending = (modal, promptEl, nameEl, validateBtn, pending) => {
  modal.classList.toggle('is-pending', pending);
  setPromptEnabled(promptEl, !pending);
  nameEl.disabled = pending;
  if (pending) validateBtn.disabled = true;
};

export const buildSpriteCreditLine = async (characterId) => {
  const characters = HangoutState.getInstance()?.state?.allCharacters || [];
  await Promise.all([loadCreditPricing(), syncSpriteCredits()]);
  return creditLine(characters, characterId);
};

const creditLine = (characters, characterId) => {
  const cost = computeImageUnitCost({
    characterHasLora: getCharacterHasLora(characterId, characters),
    actionHasLora: false,
    style: getCharacterStyle(characterId, characters)
  });
  const result = buildLocalCreditResult(cost);
  return {
    cost,
    hasEnough: result.has_enough_credits,
    text: formatCreditsUsageLine({
      total: cost,
      available: result.available_credits,
      unitCount: 1,
      template: '1 image = {cost} credits (You have {available} credits)'
    })
  };
};

const ensureModal = () => {
  const existing = document.getElementById(MODAL_ID);
  if (existing) return existing;

  const modal = document.createElement('div');
  modal.id = MODAL_ID;
  modal.className = 'message-image-prompt-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'message-image-prompt-title');
  modal.innerHTML = `
    <div class="message-image-prompt-modal__panel">
      <h2 id="message-image-prompt-title" class="message-image-prompt-modal__title">Create image for this message</h2>
      <p class="message-image-prompt-modal__status" id="message-image-prompt-status" aria-live="polite"></p>
      <label class="message-image-prompt-modal__label" for="message-image-prompt-text">Prompt</label>
      <textarea id="message-image-prompt-text" class="message-image-prompt-modal__textarea" rows="6" aria-label="Image prompt" placeholder="1girl, looking at viewer, blush, light smile"></textarea>
      <div class="prompt-helper" id="message-image-prompt-helper" aria-label="Prompt helpers">
        <div class="prompt-helper__buttons" role="tablist" aria-label="Prompt helper categories">
          <button type="button" class="prompt-helper__button" data-prompt-helper-group="action" aria-pressed="false" tabindex="0">Action</button>
          <button type="button" class="prompt-helper__button" data-prompt-helper-group="view" aria-pressed="false" tabindex="0">View</button>
          <button type="button" class="prompt-helper__button" data-prompt-helper-group="quality" aria-pressed="false" tabindex="0">Quality</button>
          <button type="button" class="prompt-helper__button" data-prompt-helper-group="background" aria-pressed="false" tabindex="0">Background</button>
          <button type="button" class="prompt-helper__button" data-prompt-helper-group="nsfw" aria-pressed="false" tabindex="0">NSFW</button>
        </div>
        <div id="message-image-prompt-pills" class="prompt-helper__pills" aria-live="polite"></div>
      </div>
      <label class="message-image-prompt-modal__label" for="message-image-prompt-name">Name</label>
      <input id="message-image-prompt-name" class="message-image-prompt-modal__input" type="text" maxlength="50" aria-label="Image name" placeholder="standing-smile" />
      <p id="message-image-prompt-credit" class="pro-tip-text" aria-live="polite">
        <span class="pro-tip-text__label">Pro tip:</span>
        <span data-credit-line>1 image = 1 credit</span>
      </p>
      <div class="message-image-prompt-modal__actions">
        <button type="button" id="message-image-prompt-cancel" class="btn" tabindex="0">Cancel</button>
        <button type="button" id="message-image-prompt-validate" class="btn btn-primary" tabindex="0">Validate</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  initPromptHelpers({
    helperRootId: 'message-image-prompt-helper',
    pillsRootId: 'message-image-prompt-pills',
    textareaId: 'message-image-prompt-text'
  });
  return modal;
};

const closeModal = (modal) => {
  modal.classList.remove('show', 'is-pending');
  modal.hidden = true;
};

export const openMessageImagePromptModal = async (spriteData) => {
  const conversationId = HangoutState.getInstance()?.getConversationId?.();
  const path = typeof spriteData?.path === 'string' ? spriteData.path.trim() : '';
  if (!conversationId || !path) {
    await showNotification('Missing conversation id. Please refresh and try again.', 'error');
    return;
  }

  const modal = ensureModal();
  const statusEl = modal.querySelector('#message-image-prompt-status');
  const promptEl = modal.querySelector('#message-image-prompt-text');
  const nameEl = modal.querySelector('#message-image-prompt-name');
  const creditEl = modal.querySelector('#message-image-prompt-credit');
  const creditLineEl = creditEl?.querySelector('[data-credit-line]');
  const cancelBtn = modal.querySelector('#message-image-prompt-cancel');
  const validateBtn = modal.querySelector('#message-image-prompt-validate');
  if (!(promptEl instanceof HTMLTextAreaElement) || !(nameEl instanceof HTMLInputElement)) return;
  if (!(cancelBtn instanceof HTMLButtonElement) || !(validateBtn instanceof HTMLButtonElement)) return;

  await ensurePromptAutocomplete();
  promptEl.value = '';
  nameEl.value = '';
  setFormPending(modal, promptEl, nameEl, validateBtn, true);
  if (statusEl) statusEl.textContent = 'Writing a prompt...';
  modal.hidden = false;
  modal.classList.add('show');

  const characters = HangoutState.getInstance()?.state?.allCharacters || [];
  await Promise.all([loadCreditPricing(), syncSpriteCredits()]);
  const credits = creditLine(characters, spriteData.characterId);
  if (creditLineEl) creditLineEl.textContent = credits.text;
  creditEl?.classList.toggle('insufficient', !credits.hasEnough);

  const handleCancel = () => closeModal(modal);
  cancelBtn.onclick = handleCancel;
  modal.onclick = (event) => {
    if (event.target === modal) handleCancel();
  };

  let draft = null;
  try {
    const response = await makeWorkerRequest(hangoutWorkerUrl('/v1/hangout/message/image-prompt'), {
      conversationId: String(conversationId).trim(),
      path
    });
    draft = await response.json();
  } catch (error) {
    if (statusEl) statusEl.textContent = '';
    await showNotification(error?.message || 'Could not write an image prompt.', 'error');
    closeModal(modal);
    return;
  }

  if (!draft?.prompt || !draft?.name) {
    if (statusEl) statusEl.textContent = '';
    await showNotification(draft?.error || 'Could not write an image prompt.', 'error');
    closeModal(modal);
    return;
  }

  promptEl.value = draft.prompt;
  nameEl.value = draft.name;
  setFormPending(modal, promptEl, nameEl, validateBtn, false);
  validateBtn.disabled = !credits.hasEnough;
  if (statusEl) statusEl.textContent = '';

  validateBtn.onclick = async () => {
    const promptText = promptEl.value.trim();
    const spriteName = formatSpriteNameForPublish(nameEl.value);
    if (!promptText || !spriteName) {
      await showNotification('Enter a prompt and a name.', 'error');
      return;
    }
    const blocked = await getBlockedFilteredImagePromptMessage(promptText);
    if (blocked) {
      await showNotification(blocked, 'error');
      return;
    }
    const unrecognized = await getBlockedUnrecognizedPromptMessage(promptText);
    if (unrecognized) {
      await showNotification(unrecognized, 'error');
      return;
    }

    setFormPending(modal, promptEl, nameEl, validateBtn, true);
    if (statusEl) statusEl.textContent = 'Creating image...';
    try {
      const token = await tokenManager.getValidAccessToken();
      if (!token) throw new Error('No valid access token');
      await fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/schedule-sprite-from-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: {
          character_id: spriteData.characterId,
          outfit_id: spriteData.outfitId ?? 0,
          action_name: spriteName,
          action_prompt: promptText
        }
      });
      await showNotification('Image scheduled for creation!', 'success');
      closeModal(modal);
    } catch (error) {
      const message = error?.status === 409
        ? 'Image already exists'
        : (error?.body?.error || error?.body?.message || error?.message || 'Failed to create image');
      await showNotification(message, 'error');
      setFormPending(modal, promptEl, nameEl, validateBtn, false);
      validateBtn.disabled = !credits.hasEnough;
      if (statusEl) statusEl.textContent = '';
    }
  };
};
